// src/auth/SessionProvider.tsx
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { fetchSessionClient, type UserSession } from "../lib/session";

export type SessionContextValue = {
  session: UserSession | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined
);

export function SessionProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<UserSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSessionClient();
      setSession(data);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Unable to verify your session.";
      setError(message);
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      loading,
      error,
      refresh: load
    }),
    [session, loading, error, load]
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

// ⚠️ THIS MUST **NOT** THROW ANYMORE
export function useSessionContext(): SessionContextValue {
  const ctx = useContext(SessionContext);

  if (!ctx) {
    // In dev, log a warning so you can see when something renders you outside <SessionProvider>
    if (typeof window !== "undefined" && import.meta.env?.DEV) {
      console.warn(
        "⚠️ useSessionContext called outside SessionProvider. " +
          "Returning fallback (unauthenticated) session."
      );
    }

    // Safe fallback – prevents the crash
    return {
      session: null,
      loading: false,
      error: "SessionProviderMissing",
      refresh: async () => {}
    };
  }

  return ctx;
}